import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { ApiError } from '../api';
import { paymentEndpoints } from '../api-payments';
import type { BalancesResponse } from '../api-payments';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, EmptyRow, ErrorNote, Panel, Table } from '../ui';
import { dollars, humanize } from './event/shared';
import { paymentStatusTone } from './OrderPayment';

/**
 * Admin → Balances (D-71, docs/payment-requests.md §7): every committed order
 * still owed on, what is past due and how old it is. Operational receivables
 * for ticket orders, not a replacement for QuickBooks A/R. The Outstanding
 * Balances and Past Due Orders reports export the same rows.
 */
export default function BalancesPage() {
  const can = usePermissions();
  const [pastDue, setPastDue] = useState(false);
  const balances = useQuery<BalancesResponse, ApiError>({ queryKey: ['balances', pastDue], queryFn: () => paymentEndpoints.balances({ past_due: pastDue }), enabled: can('orders.view') });
  if (!can('orders.view')) return <NotPermitted permission="orders.view" />;
  const data = balances.data;

  return (
    <div className="space-y-4">
      {balances.error ? <ErrorNote>{balances.error.message}</ErrorNote> : null}
      <Panel title="Balances" description="Orders with money still due. Tickets on these orders follow each order's issuance policy until paid.">
        <div className="flex flex-wrap gap-8 px-5 py-4">
          <Stat label="Outstanding" value={dollars(data?.summary.outstanding_cents ?? 0)} detail={`${data?.summary.outstanding_orders ?? 0} orders`} />
          <Stat label="Past due" value={dollars(data?.summary.past_due_cents ?? 0)} tone={data?.summary.past_due_cents ? 'text-bad' : ''} />
          <Stat label="Due in the next 7 days" value={dollars(data?.summary.due_this_week_cents ?? 0)} />
        </div>
      </Panel>

      <Panel title="Aging" description="By days past the order's due date. Orders with no due date, or not yet due, are current.">
        <Table headers={['Bucket', 'Orders', 'Balance']}>
          {(data?.aging ?? []).map((row) => (
            <tr key={row.bucket} className="border-b border-line last:border-0">
              <td className="px-5 py-2 text-sm">{row.bucket}</td>
              <td className="tabular px-5 py-2 text-sm">{row.orders}</td>
              <td className={`tabular px-5 py-2 text-sm ${row.bucket !== 'Current' && row.balance_cents ? 'font-medium text-bad' : ''}`}>{dollars(row.balance_cents)}</td>
            </tr>
          ))}
        </Table>
      </Panel>

      <Panel
        title="Outstanding orders"
        actions={
          <label className="flex items-center gap-2 text-sm text-ink-600">
            <input type="checkbox" checked={pastDue} onChange={(e) => setPastDue(e.target.checked)} /> Past due only
          </label>
        }
      >
        <Table headers={['Order', 'Customer', 'Event', 'Total', 'Paid', 'Balance', 'Due', 'Status', 'Link']}>
          {!data || data.orders.length === 0 ? (
            <EmptyRow columns={9}>{balances.isLoading ? 'Loading…' : 'Nothing outstanding.'}</EmptyRow>
          ) : (
            data.orders.map((o) => (
              <tr key={o.order_id} className="border-b border-line last:border-0">
                <td className="px-5 py-2">
                  <Link to={`/orders/${o.order_id}`} className="font-mono font-medium text-accent hover:underline">
                    {o.order_number}
                  </Link>
                  <div className="text-xs text-ink-400">{humanize(o.sales_channel)}</div>
                </td>
                <td className="px-5 py-2 text-sm">
                  {o.customer ?? '—'}
                  <div className="text-xs text-ink-400">{o.email ?? ''}</div>
                </td>
                <td className="px-5 py-2 text-sm">
                  {/* A package order names its package; its shows are on the order (D-330). */}
                  {o.package_name ? (
                    <>
                      {o.package_name}
                      <div className="text-xs text-ink-400">Package</div>
                    </>
                  ) : (
                    <>
                      {o.event_title ?? '—'}
                      <div className="text-xs text-ink-400">{o.performance_starts_at ? o.performance_starts_at.slice(0, 10) : ''}</div>
                    </>
                  )}
                </td>
                <td className="tabular px-5 py-2 text-sm">{dollars(o.total_cents)}</td>
                <td className="tabular px-5 py-2 text-sm">{dollars(o.paid_cents)}</td>
                <td className="tabular px-5 py-2 text-sm font-medium">{dollars(o.balance_cents)}</td>
                <td className={`px-5 py-2 text-sm whitespace-nowrap ${o.days_past_due > 0 ? 'text-bad' : ''}`}>
                  {o.payment_due_at ? o.payment_due_at.slice(0, 10) : '—'}
                  {o.days_past_due > 0 ? <div className="text-xs">{o.days_past_due} days late</div> : null}
                </td>
                <td className="px-5 py-2">
                  <Badge tone={paymentStatusTone(o.payment_status)}>{humanize(o.payment_status)}</Badge>
                </td>
                <td className="px-5 py-2 text-xs text-ink-600">{o.link_status ? `${humanize(o.link_status)}${o.link_sent_at ? ` · sent ${o.link_sent_at.slice(0, 10)}` : ' · not emailed'}` : 'none'}</td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}

function Stat({ label, value, detail, tone = '' }: { label: string; value: string; detail?: string; tone?: string }) {
  return (
    <div>
      <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</p>
      <p className={`tabular text-2xl font-semibold ${tone || 'text-ink-900'}`}>{value}</p>
      {detail ? <p className="text-xs text-ink-400">{detail}</p> : null}
    </div>
  );
}
